import React from 'react'
import clsx from 'clsx'
import { Trans, useTranslation } from 'react-i18next'
import { AnalyticsElement } from '@typesApp/Analytics'
import { Switch } from '@components/ui-css/Switch'
import { ButtonLink } from '@components/ui-css/ButtonLink'
import { useEnvironment } from '@hooks/useEnvironment'
import { useInsuranceToggle } from './hooks/useInsuranceToggle'
import { useInsuranceToggleMessage } from './hooks/useInsuranceToggleMessage'
import { useEligibleProductCheck } from '@hooks/useEligibleProductCheck'
import { Button } from '@components/ui-css/Button'
import { Loader } from '@components/ui-css/Loader'
import { injectAnalytics } from '@utils/analytics'
import { injectDataAttributes } from '@utils/dataAttributes'
import styles from './styles/InsuranceToggle.module.scss'

const InsuranceToggleContainer: React.FC = () => {
  const { cartPath, translationNamespace } = useEnvironment()
  const { t } = useTranslation(translationNamespace, { keyPrefix: 'InsuranceToggle' })
  const { isLoading, isUpdatingToggle, toggleState, onToggleClick, onShowBenefits, onToggleChange, onSelectProduct } =
    useInsuranceToggle()
  const { messageKey, isLoading: isMessageLoading } = useInsuranceToggleMessage()

  useEligibleProductCheck({ disableInitialCheck: !window.location.pathname.includes(cartPath) })

  const { toggleOn, allowSelectProduct } = toggleState
  const showBenefitsLink = toggleOn && !isLoading
  const showLoader = isLoading || isUpdatingToggle
  const showSwitch = !isLoading && !isUpdatingToggle
  const showMessage = !isLoading && !isMessageLoading && messageKey

  return (
    <div className={styles.container} {...injectDataAttributes(`toggle.${toggleOn ? 'toggleOn' : 'toggleOff'}`)}>
      <div className={styles.switchContainer}>
        <div className={clsx(styles.labelContent, toggleOn && styles.synced)}>
          {!toggleOn && t('label')}
          {showBenefitsLink && <BenefitsLink onClick={onShowBenefits} />}
        </div>
        {showLoader && <Loader variant="dark" />}
        {showSwitch && <Switch checked={toggleOn} onChange={onToggleChange} onClick={onToggleClick} />}
      </div>
      {showMessage && <div className={styles.error}>{t(`error.${messageKey}`)}</div>}
      {allowSelectProduct && !isLoading && (
        <Button
          disabled={isUpdatingToggle}
          variant="outlined"
          onClick={onSelectProduct}
          {...injectDataAttributes('toggle.selectProduct')}
          {...injectAnalytics(AnalyticsElement.OTHER)}
        >
          {t('select-product')}
        </Button>
      )}
    </div>
  )
}

type BenefitsLinkProps = {
  onClick: () => void
}

const BenefitsLink: React.FC<BenefitsLinkProps> = props => {
  const { onClick } = props
  const { translationNamespace } = useEnvironment()

  return (
    <Trans
      i18nKey="InsuranceToggle.member-found-label"
      ns={translationNamespace}
      components={{
        component0: (
          <ButtonLink
            className={styles.benefitsLink}
            underline
            onClick={onClick}
            {...injectDataAttributes('toggle.benefitsLink')}
            {...injectAnalytics(AnalyticsElement.MORE_INFO)}
          />
        ),
      }}
    />
  )
}

export default InsuranceToggleContainer
